import React, { useState, useEffect, createContext } from 'react'
import { useHistory, useParams } from 'react-router'
import {
  Dialog,
  Box,
  Card,
  CardContent,
  Link,
  Tooltip,
  Button,
  Paper,
} from '@mui/material'

import TableBulkActions from './tableBulkActions'
import OrderModalContent from './orderModal'
import { usePoListQuery } from 'services/hooks/purchaseOrders/usePoListQuery'
import {
  DataGrid,
  GridColDef,
  GridPagination,
  GridRenderCellParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid'
import StatusLabel from 'components/tables/statusLabel'
import AssignDropdown from 'components/assignDropdown'
import DataGridCustomPagination from 'components/dataGridCustomPagination'
import { usePoAssignUsersQuery } from 'services/hooks/purchaseOrders/usePoAssignUsersQuery'
import Filters from './filters'
import { usePoStatusListQuery } from 'services/hooks/purchaseOrders/usePoStatusListQuery'

const PurchaseOrdersList = () => {
  const { id: poId }: any = useParams()
  const history = useHistory()

  const [filterValues, setFilterValues] = useState({
    statusIds: [],
    supplierIds: [],
    users: [],
    term: '',
  })
  const [orderModalOpen, setOrderModalOpen] = useState(false)
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 25,
  })
  const { data: poListData, isFetching: isFetchingPoList } = usePoListQuery({
    pageIndex: paginationModel.page,
    rowsCount: paginationModel.pageSize,
    filterValues,
  })

  const { data: statusList } = usePoStatusListQuery()
  const { data: poAssignUsersList } = usePoAssignUsersQuery()

  const [rowSelectionModel, setRowSelectionModel] =
    React.useState<GridRowSelectionModel>([])

  const rowCountRef = React.useRef(poListData?.total_rows || 0)

  const rowCount = React.useMemo(() => {
    if (poListData?.total_rows !== undefined) {
      rowCountRef.current = poListData.total_rows
    }
    return rowCountRef.current
  }, [poListData?.total_rows])

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'PO',
      renderCell: (params: GridRenderCellParams) => (
        <Link
          component='button'
          variant='body2'
          onClick={() => openPoModal(params.row)}
        >
          {params.row.id}
        </Link>
      ),
      minWidth: 150,
    },
    {
      field: 'date',
      headerName: 'Date',
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.row.date_added.formatted_date}>
          <span>{params.row.date_added.human_date.relative.long}</span>
        </Tooltip>
      ),
      minWidth: 200,
      valueGetter: (value, row) => row.date_added.formatted_date,
    },
    {
      field: 'part_numbers',
      headerName: 'Part Number',
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          {params.row.part_numbers[0]}{' '}
          {params.row.part_numbers.length > 1 && (
            <Tooltip title={params.row.part_numbers.join(', ')}>
              <Button color='primary'>
                {params.row.part_numbers.length - 1} more...
              </Button>
            </Tooltip>
          )}
        </Box>
      ),
      minWidth: 200,
      valueGetter: (value, row) => row.part_numbers[0],
    },
    {
      field: 'requisition_ids',
      headerName: 'Requisition ID',
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          {params.row.requisition_ids[0]}{' '}
          {params.row.requisition_ids.length > 1 && (
            <Tooltip title={params.row.requisition_ids.join(', ')}>
              <Button color='primary'>
                {params.row.requisition_ids.length - 1} more...
              </Button>
            </Tooltip>
          )}
        </Box>
      ),
      minWidth: 200,
      valueGetter: (value, row) => row.requisition_ids[0],
    },
    {
      field: 'supplier',
      headerName: 'Supplier',
      minWidth: 150,
    },
    {
      field: 'status',
      headerName: 'Status',
      renderCell: (params: GridRenderCellParams) =>
        statusList && (
          <StatusLabel statusList={statusList} statusId={params.row.status_id}>
            {params.row.status}
          </StatusLabel>
        ),
      display: 'flex',
      minWidth: 200,
      valueGetter: (value, row) => row.status,
    },
    {
      field: 'assignedTo',
      headerName: 'AssignedTo',
      renderCell: (params: GridRenderCellParams) =>
        poAssignUsersList && (
          <AssignDropdown
            userList={poAssignUsersList}
            selected={params.row}
            assign={params.row.permissions.assign}
            updateFor='po'
          />
        ),
      minWidth: 200,
      display: 'flex',
      valueGetter: (value, row) => row.assignee?.name,
    },
  ]

  const openPoModal = (po) => {
    history.push(`/po/${po.id}/lines`)
  }

  useEffect(() => {
    if (poId) {
      setOrderModalOpen(true)
    } else {
      setOrderModalOpen(false)
    }
  }, [poId])

  const closeModal = () => {
    setOrderModalOpen(false)
    history.push('/po/list')
  }

  const onChangeFilter = (target, values) => {
    setFilterValues({
      ...filterValues,
      [target]: values,
    })
  }

  return (
    <Paper sx={{ p: 3 }} elevation={3}>
      <Filters onChangeFilter={onChangeFilter} filterValues={filterValues} />

      <Box mb={3} height={600}>
        <DataGrid
          checkboxSelection
          disableRowSelectionOnClick
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel)
          }}
          rowSelectionModel={rowSelectionModel}
          rows={poListData?.purchase_order || []}
          columns={columns} // Define your columns
          rowCount={rowCount}
          paginationMode='server'
          paginationModel={paginationModel}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          onPaginationModelChange={setPaginationModel}
          loading={isFetchingPoList}
          slots={{
            pagination: (props) => (
              <GridPagination
                ActionsComponent={DataGridCustomPagination}
                {...props}
              />
            ),
          }}
        />
      </Box>
      {poAssignUsersList && statusList && (
        <Box>
          <Card>
            <CardContent
              sx={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <TableBulkActions checkedPos={rowSelectionModel} />
            </CardContent>
          </Card>

          <Dialog
            maxWidth='xl'
            fullWidth
            open={orderModalOpen}
            onClose={closeModal}
          >
            {poId && <OrderModalContent onClose={closeModal} />}
          </Dialog>
        </Box>
      )}
    </Paper>
  )
}

export default PurchaseOrdersList
